import type { MetaDescriptor } from '@remix-run/react'

/**
 * Site geneli SEO sabitleri.
 * Tek kaynak (single source of truth) — tüm route'lar burayı kullanır.
 */
export const SITE_URL = 'https://aldemuhendislik.com'
export const SITE_NAME = 'Al-De Mühendislik - Alde Mühendislik'
export const SITE_LOCALE = 'tr_TR'

/**
 * Sosyal paylaşımlarda (Open Graph / Twitter Card) kullanılacak görsel.
 * Not: Şu an marka logosuna işaret eder. Ideal boyut 1200x630 PNG/JPG olmalı;
 * gerçek bir og-image üretildiğinde bu sabit güncellenmelidir.
 */
export const DEFAULT_OG_IMAGE = '/assets/AL-DE-muhendislik-logo.svg'

/**
 * HTML içeriğini düz metne çevirir (description üretimi için).
 * Meta description ~150-160 karakter olmalıdır.
 */
export const stripHtml = (html: string): string =>
  html
    .replace(/<[^>]*>?/g, '')
    .replace(/\s+/g, ' ')
    .trim()

/**
 * Verilen metni belirli bir karakter sayısına kırpar; kelime ortasında kesmez.
 */
export const truncate = (text: string, max = 160): string => {
  if (text.length <= max) return text
  const slice = text.slice(0, max)
  const lastSpace = slice.lastIndexOf(' ')
  return `${slice.slice(0, lastSpace > 0 ? lastSpace : max).trim()}...`
}

/**
 * Bir sayfanın description'ı için HTML içeriğinden özet üretir.
 */
export const descriptionFromHtml = (html: string, max = 160): string =>
  truncate(stripHtml(html), max)

type BuildMetaArgs = {
  title: string
  description: string
  path: string
  image?: string
  type?: 'website' | 'article'
}

/**
 * Bir route için tutarlı SEO meta etiketleri üretir.
 *
 * Ürettiği etiketler:
 * - <title>
 * - <meta name="description">
 * - <link rel="canonical">
 * - Open Graph: og:title, og:description, og:url, og:image, og:type, og:site_name, og:locale
 * - Twitter Card: twitter:card, twitter:title, twitter:description, twitter:image
 *
 * Kullanımı (route dosyasında):
 *   export const meta: MetaFunction = () => buildMeta({
 *     title: "...",
 *     description: "...",
 *     path: "/hizmetlerimiz",
 *   })
 */
export const buildMeta = ({
  title,
  description,
  path,
  image = DEFAULT_OG_IMAGE,
  type = 'website',
}: BuildMetaArgs): MetaDescriptor[] => {
  const url = `${SITE_URL}${path}`
  const imageUrl = image.startsWith('http') ? image : `${SITE_URL}${image}`

  return [
    { title },
    { name: 'description', content: description },
    { tagName: 'link', rel: 'canonical', href: url },
    // Open Graph
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:url', content: url },
    { property: 'og:image', content: imageUrl },
    { property: 'og:type', content: type },
    { property: 'og:site_name', content: SITE_NAME },
    { property: 'og:locale', content: SITE_LOCALE },
    // Twitter Card
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
    { name: 'twitter:image', content: imageUrl },
  ]
}
